import { useState } from "react";
import { Plus, Pencil, Trash2, Loader2, ArrowUp, ExternalLink, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { UploadBtn } from "@/components/admin/SermonForm";
import { field, card } from "@/components/admin/SettingsPanel";
import { MenuOrder } from "@/components/admin/MenuOrder";

const BUILT_IN = [["about", "About"], ["sermons", "Messages"], ["donate", "Give"], ["contact", "Contact"]];
const EMPTY_SECTION = { heading_en: "", heading_mh: "", body_en: "", body_mh: "", image_url: "" };
const EMPTY = { title_en: "", title_mh: "", slug: "", intro_en: "", intro_mh: "", hero_url: "", sections: [{ ...EMPTY_SECTION }], show_in_menu: true, published: true, order: 0 };
const sw = "data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-[#3A4558]";
const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const SectionEditor = ({ s, i, onChange, onRemove, onUp }) => {
  const set = (k) => (e) => onChange({ ...s, [k]: e.target.value });
  return (
    <div data-testid={`page-section-${i}`} className="grid gap-2 rounded-xl border border-[#F4EEE2]/10 p-3 sm:grid-cols-2">
      <div className="flex items-center justify-between sm:col-span-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#9AA4B4]">Section {i + 1}</span>
        <span className="flex gap-1">
          {i > 0 && <button type="button" data-testid={`page-section-up-${i}`} onClick={onUp} className="rounded-full p-1.5 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>}
          <button type="button" data-testid={`page-section-remove-${i}`} onClick={onRemove} className="rounded-full p-1.5 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Remove section"><X className="h-4 w-4" /></button>
        </span>
      </div>
      <input data-testid={`page-section-heading-en-${i}`} placeholder="Heading (English)" value={s.heading_en} onChange={set("heading_en")} className={field} />
      <input placeholder="Heading (Marshallese)" value={s.heading_mh} onChange={set("heading_mh")} className={field} />
      <textarea data-testid={`page-section-body-en-${i}`} rows={4} placeholder="Text (English)" value={s.body_en} onChange={set("body_en")} className={field} />
      <textarea rows={4} placeholder="Text (Marshallese)" value={s.body_mh} onChange={set("body_mh")} className={field} />
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        {s.image_url && <img src={fileUrl(s.image_url)} alt="" className="h-14 w-14 rounded-lg object-cover" />}
        <UploadBtn accept="image/*" testid={`page-section-image-${i}`} label={s.image_url ? "Replace photo" : "Add photo"} onDone={(u) => onChange({ ...s, image_url: u })} />
        {s.image_url && <button type="button" onClick={() => onChange({ ...s, image_url: "" })} className="font-ui text-xs text-[#F2A08A]">Remove photo</button>}
      </div>
    </div>
  );
};

const PageForm = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState(initial?.id ? { ...EMPTY, ...initial } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value, ...(k === "title_en" && !initial?.id ? { slug: toSlug(e.target.value) } : {}) }));
  const secs = (fn) => setF((p) => ({ ...p, sections: fn([...p.sections]) }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k]]));
      const res = initial?.id ? await api.put(`/pages/${initial.id}`, body) : await api.post("/pages", body);
      if (res.data?.pending) toast("Sent for approval — it will appear once a Super Admin approves it");
      else toast.success("Page saved — live on the site");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent data-testid="page-form-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto border-[#D4AF37]/20 bg-[#131C2E] text-[#F4EEE2]">
        <DialogHeader><DialogTitle className="font-display text-3xl">{initial?.id ? "Edit page" : "New page"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <input data-testid="page-title-en" required placeholder="Page title (English)" value={f.title_en} onChange={set("title_en")} className={field} />
          <input data-testid="page-title-mh" placeholder="Page title (Marshallese)" value={f.title_mh} onChange={set("title_mh")} className={field} />
          <label className="flex items-center gap-2 rounded-xl border border-[#F4EEE2]/15 px-3 text-sm text-[#9AA4B4] sm:col-span-2">/p/<input data-testid="page-slug" required value={f.slug} onChange={set("slug")} placeholder="page-address" className="w-full bg-transparent py-2.5 text-[#F4EEE2] outline-none" /></label>
          <textarea data-testid="page-intro-en" rows={2} placeholder="Short intro (English)" value={f.intro_en} onChange={set("intro_en")} className={field} />
          <textarea rows={2} placeholder="Short intro (Marshallese)" value={f.intro_mh} onChange={set("intro_mh")} className={field} />
          <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
            {f.hero_url && <img src={fileUrl(f.hero_url)} alt="" className="h-14 w-20 rounded-lg object-cover" />}
            <UploadBtn accept="image/*" testid="page-hero-upload" label={f.hero_url ? "Replace top photo" : "Add top photo"} onDone={(u) => setF((p) => ({ ...p, hero_url: u }))} />
            {f.hero_url && <button type="button" onClick={() => setF((p) => ({ ...p, hero_url: "" }))} className="font-ui text-xs text-[#F2A08A]">Remove photo</button>}
          </div>
          <div className="grid gap-3 sm:col-span-2">
            {f.sections.map((s, i) => (
              <SectionEditor key={i} s={s} i={i} onChange={(v) => secs((a) => { a[i] = v; return a; })} onRemove={() => secs((a) => a.filter((_, j) => j !== i))} onUp={() => secs((a) => { [a[i - 1], a[i]] = [a[i], a[i - 1]]; return a; })} />
            ))}
            <button type="button" data-testid="page-add-section-btn" onClick={() => secs((a) => [...a, { ...EMPTY_SECTION }])} className="flex w-fit items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 font-ui text-xs font-semibold text-[#F4EEE2]"><Plus className="h-3.5 w-3.5" /> Add section</button>
          </div>
          <label className="flex items-center justify-between rounded-xl border border-[#F4EEE2]/10 px-4 py-3 text-sm">Show in the menu <Switch data-testid="page-menu-toggle" checked={f.show_in_menu} onCheckedChange={(v) => setF({ ...f, show_in_menu: v })} className={sw} /></label>
          <label className="flex items-center justify-between rounded-xl border border-[#F4EEE2]/10 px-4 py-3 text-sm">Published (visible) <Switch data-testid="page-published-toggle" checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} className={sw} /></label>
          <button data-testid="page-save-btn" disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] py-3 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60 sm:col-span-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save page</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const PagesPanel = ({ canBuiltIn }) => {
  const { pages, settings, setSettings, refresh, on } = useSite();
  const [editing, setEditing] = useState(null);
  const toggleBuiltIn = async (k, v) => {
    try {
      const { data } = await api.put("/settings", { sections: { ...settings.sections, [k]: v } });
      setSettings((p) => ({ ...p, ...data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete the page "${p.title_en}"? (You can undo this in Activity.)`)) return;
    await api.delete(`/pages/${p.id}`).then((r) => { if (r.data?.pending) toast("Delete sent for approval"); else toast.success("Page deleted"); refresh(); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  return (
    <div data-testid="pages-panel" className="grid gap-6">
      <MenuOrder />
      {canBuiltIn && (
        <div className={card}>
          <h3 className="font-display text-2xl text-[#F4EEE2]">Built-in pages</h3>
          <p className="mt-1 text-sm text-[#B7BFCC]">Turn a page off to remove it from the website and menu. Turn it back on anytime.</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {BUILT_IN.map(([k, l]) => (
              <label key={k} className="flex items-center justify-between rounded-xl border border-[#F4EEE2]/10 px-4 py-3 text-sm">{l} <Switch data-testid={`builtin-toggle-${k}`} checked={on(k)} onCheckedChange={(v) => toggleBuiltIn(k, v)} className={sw} /></label>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-2xl text-[#F4EEE2]">Your pages</h3>
          <button data-testid="page-add-btn" onClick={() => setEditing({})} className="flex items-center gap-2 rounded-full bg-[#F4EEE2] px-5 py-2.5 font-ui text-sm font-semibold text-[#0B111E] hover:bg-[#D4AF37]"><Plus className="h-4 w-4" /> New page</button>
        </div>
        {!pages.length && <p className="py-8 text-sm text-[#B7BFCC]">No pages yet. Create one — for example "Our Pastors", "Events" or "Prayer".</p>}
        {pages.map((p) => (
          <div key={p.id} data-testid={`page-row-${p.slug}`} className="flex items-center gap-3 border-b border-[#F4EEE2]/10 py-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-ui font-semibold text-[#F4EEE2]">{p.title_en}</p>
              <p className="truncate text-xs text-[#9AA4B4]">/p/{p.slug} · {p.published ? "Published" : "Hidden"}{p.show_in_menu ? " · In menu" : ""}</p>
            </div>
            {p.published && <Link to={`/p/${p.slug}`} data-testid={`page-view-${p.slug}`} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="View"><ExternalLink className="h-4 w-4" /></Link>}
            <button data-testid={`page-edit-${p.slug}`} onClick={() => setEditing(p)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`page-delete-${p.slug}`} onClick={() => del(p)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {editing && <PageForm initial={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
};
